import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLines } from "./Reveal";
import { IMAGES } from "@/constants/images";

const Tile = ({ src, alt, kicker, title, copy, className, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className={`corner-frame group relative overflow-hidden ${className}`}>
      <motion.img
        style={{ y }}
        src={src}
        alt={alt}
        loading="lazy"
        data-testid={testid}
        className="h-full w-full scale-110 object-cover transition-transform duration-[1200ms] group-hover:scale-[1.18]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/25 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-7">
        <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
          {kicker}
        </span>
        <h3 className="mt-2 font-['Playfair_Display'] text-2xl text-[#f5f5f0] sm:text-3xl">{title}</h3>
        {copy && <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#c9c9c2]">{copy}</p>}
      </div>
    </div>
  );
};

export const Aspiration = () => (
  <section className="relative border-t border-white/10 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="max-w-3xl">
        <Reveal>
          <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">
            The Life You&rsquo;re Trading For
          </span>
        </Reveal>
        <h2 className="mt-8 font-['Playfair_Display'] text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
          <MaskLines whileInView lineClass="gold-metallic-text" lines={["This isn't a fantasy.", "It's a decision away."]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-7 text-base leading-relaxed text-[#a1a1aa] sm:text-lg">
            Superyachts on the Med. A private island in the Pacific. The supercar in the driveway
            and the penthouse above the city. When you finally trade with a real edge and a coached
            mind, the money stops being the scoreboard &mdash; it becomes the fuel for a life like this.
          </p>
        </Reveal>
      </div>

      {/* Full-width beach banner */}
      <Reveal delay={0.1} className="mt-14">
        <Tile
          testid="aspiration-beach"
          src={IMAGES.beach}
          alt="Gold coins on a Pacific beach beside a glass of iced tea overlooking the ocean"
          kicker="The Quiet Payoff"
          title="Gold On The Sand, Ocean In View"
          copy="A glass of tea, a pile of gold, and nothing on the calendar. This is what trading well eventually buys you — time."
          className="aspect-[16/9] w-full sm:aspect-[21/8]"
        />
      </Reveal>

      {/* Block 1 */}
      <div className="mt-14 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-12 lg:grid-rows-2 lg:h-[760px]">
        <Reveal className="lg:col-span-8">
          <Tile
            testid="aspiration-office"
            src={IMAGES.office}
            alt="Ultra-premium trading office with a large multi-monitor stock trading setup"
            kicker="The Command Desk"
            title="Trade From The Top Floor"
            copy="A premium office, a wall of live charts, a skyline below you — the environment of a trader who broke through."
            className="aspect-[16/10] lg:aspect-auto lg:h-full"
          />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4 lg:row-span-2">
          <Tile
            testid="aspiration-party"
            src={IMAGES.party}
            alt="Fabulous exclusive luxury night party"
            kicker="Front-Row Nights"
            title="Celebrate The Wins"
            copy="Champagne towers and skyline parties. The upside of discipline is a life worth showing up for."
            className="aspect-[3/4] lg:aspect-auto lg:h-full"
          />
        </Reveal>
        <Reveal delay={0.2} className="lg:col-span-8">
          <Tile
            testid="aspiration-island"
            src={IMAGES.island}
            alt="Luxury private Pacific Ocean island retreat"
            kicker="Private Pacific Islands"
            title="Work From Paradise"
            copy="Overwater villas and turquoise water. When your trading works, geography becomes optional."
            className="aspect-[16/10] lg:aspect-auto lg:h-full"
          />
        </Reveal>
      </div>

      {/* Block 2 */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-12 lg:grid-rows-2 lg:h-[760px]">
        <Reveal className="lg:col-span-8">
          <Tile
            testid="aspiration-yacht"
            src={IMAGES.yacht}
            alt="Luxury superyacht at sunset"
            kicker="Blue-Water Freedom"
            title="Set Your Own Horizon"
            copy="A superyacht at golden hour. Freedom isn't a bigger account — it's what the account finally lets you do."
            className="aspect-[16/10] lg:aspect-auto lg:h-full"
          />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4 lg:row-span-2">
          <Tile
            testid="aspiration-supercar"
            src={IMAGES.supercar}
            alt="Black luxury supercar with gold accents at a modern mansion"
            kicker="Earned, Not Leased"
            title="Drive The Proof"
            copy="The car in the driveway of a home you own outright. Quiet, undeniable proof the work paid off."
            className="aspect-[3/4] lg:aspect-auto lg:h-full"
          />
        </Reveal>
        <Reveal delay={0.2} className="lg:col-span-8">
          <Tile
            testid="aspiration-penthouse"
            src={IMAGES.penthouse}
            alt="Opulent penthouse overlooking a city skyline"
            kicker="Above The Noise"
            title="Live At The Top"
            copy="A penthouse over the skyline. The view changes when you stop fighting the market and start commanding it."
            className="aspect-[16/10] lg:aspect-auto lg:h-full"
          />
        </Reveal>
      </div>

      {/* Wealth banner */}
      <Reveal delay={0.1} className="mt-4 sm:mt-6">
        <Tile
          testid="aspiration-wealth"
          src={IMAGES.wealth}
          alt="Luxury watch and wealth flatlay"
          kicker="Keep Score In Real Assets"
          title="The Numbers Become Nice Problems"
          className="aspect-[16/9] w-full sm:aspect-[21/8]"
        />
      </Reveal>
    </div>
  </section>
);
